import { uniqBy } from 'lodash-es';
import { Cart } from '@lambda/commons/apis/shopify/types/storefront';
import { SearchOffers } from '@lambda/commons/apis/elasticsearch/types';
import { Logger } from '@lambda/commons/logging/logger';
import { RC_DURATION } from '@lambda/reebelo';
import { extractTags } from '@lambda/reebelo/src/tagHelpers';
import {
  CartItemAttribute,
  generateIDAttr,
  encodeDecodeForAttr,
  generateAddCartDateTimeAttr,
} from '@/lib/use-cart';

import {
  RecommendationsT,
  AddToCartArgs,
  QtyChangeArgs,
  RecommendedItemT,
  RemoveFromCartArgs,
  AddToCartExpressShippingArgs,
} from './types';
import {
  filterOutShippingDeliveryAttributes,
  updateParentItemDeliveryAttributes,
} from '@/lib/order';
import settings from '@/settings';
import { trackReebeloCareAdded } from '@/lib/analytics/trackProduct';
import { ReebeloCareTrackRequest } from '@/lib/analytics/types';

const GID_VAR = 'gid://shopify/ProductVariant/';
const getMerchandiseId = (variantId: number) => `${GID_VAR}${variantId}`;
const logger = new Logger('v2-card-recommendedItemsUtils');

export const handleAddToCart =
  ({
    add,
    loading,
    quantity,
    variantId,
    attributes,
    setScrollPosOnCartChange,
    callback,
    type,
    parentItem,
  }: AddToCartArgs): (() => void) =>
  () => {
    if (loading || !variantId) return;

    const merchandiseId = getMerchandiseId(variantId);

    if (setScrollPosOnCartChange) setScrollPosOnCartChange();
    add({
      quantity,
      attributes,
      merchandiseId,
    }).then(() => {
      if (callback) callback();

      if (type === 'warranty') {
        const { psku } = extractTags(
          parentItem?.merchandise.product.tags ?? [],
        );
        const trackingProps: ReebeloCareTrackRequest = {
          entry_point: 'Cart',
          reebelocare_id: variantId,
          device_psku: psku ?? '',
          device_vendor: parentItem?.merchandise.product.vendor ?? '',
          device_sku: parentItem?.merchandise.sku ?? '',
          duration: RC_DURATION['24_MONTH'],
        };

        trackReebeloCareAdded(trackingProps);
      }
    });
  };

export const handleAddToCartExpressShipping =
  ({
    add,
    update,
    price,
    title,
    loading,
    quantity,
    variantId,
    dispatchMin,
    dispatchMax,
    minDeliveryDate,
    maxDeliveryDate,
    cutoffTime,
    cutoffTimezone,
    zipcodeStateData,
    parentItem,
    setScrollPosOnCartChange,
  }: AddToCartExpressShippingArgs): (() => void) =>
  async () => {
    try {
      if (loading)
        throw new Error('not adding express shipping to cart -- still loading');

      if (!variantId) {
        throw new Error(
          'not adding express shipping to cart -- missing variant ID',
        );
      }

      if (!parentItem) {
        throw new Error(
          'not adding express shipping to cart -- missing parent ID',
        );
      }

      const merchandiseId = getMerchandiseId(variantId);
      const shippingAttributes = [
        { key: '_shipping_option', value: 'express' },
        {
          key: '_shipping_option_cost',
          value: price.toString(),
        },
      ];
      const deliverByAttributes = [
        {
          key: '_deliver_min',
          value: minDeliveryDate,
        },
        {
          key: '_deliver_max',
          value: maxDeliveryDate,
        },
        {
          key: '_cutoff_time',
          value: cutoffTime == null || cutoffTime === '' ? '' : cutoffTime,
        },
        {
          key: '_cutoff_timezone',
          value:
            cutoffTimezone == null || cutoffTimezone === ''
              ? ''
              : cutoffTimezone,
        },
        ...(settings.store === 'reebelo-au'
          ? [
              {
                key: '_deliver_state',
                value: zipcodeStateData?.state.toString() || '',
              },
              {
                key: '_deliver_zipcode',
                value: zipcodeStateData?.zipcode.toString() || '',
              },
            ]
          : []),
      ];

      if (setScrollPosOnCartChange) setScrollPosOnCartChange();

      // add express shipping line
      await add({
        quantity,
        attributes: [
          ...encodeDecodeForAttr.encode({
            sku: parentItem.merchandise.sku,
            title: parentItem.merchandise.product.title,
          }),
          ...generateIDAttr(
            parentItem.merchandise.product.vendor,
            parentItem.merchandise.sku,
          ),
          ...generateAddCartDateTimeAttr(),
        ],
        merchandiseId,
      });

      const filteredAttributes = filterOutShippingDeliveryAttributes(
        parentItem.attributes,
      );

      // update parent delivery attributes based on express shippping attribute
      await update([
        {
          attributes: [
            ...filteredAttributes,
            ...deliverByAttributes,
            ...shippingAttributes,
            { key: '_dispatch_min', value: dispatchMin.toString() },
            { key: '_dispatch_max', value: dispatchMax.toString() },
          ],
          id: parentItem.id,
          quantity: parentItem.quantity,
        },
      ]);
    } catch (error: any) {
      logger.error('error adding express shipping to cart', {
        title,
        price,
        quantity,
        variantId,
        parentItem,
        error,
      });
    }
  };

export const handleRemoveFromCart =
  ({
    id,
    remove,
    update,
    product,
    loading,
    type,
    parentItem,
    setScrollPosOnCartChange,
  }: RemoveFromCartArgs): (() => void) =>
  async () => {
    if (loading || !id) return;

    if (setScrollPosOnCartChange) setScrollPosOnCartChange();

    if (type === 'expressShipping' && parentItem) {
      // if express shipping item removed update back its parent delivery attributes to original state
      await updateParentItemDeliveryAttributes({
        parentItem,
        update,
        attributes: new Map<string, string>([
          ['_deliver_min', product.standardMinDeliverBy],
          ['_deliver_max', product.standardMaxDeliverBy],
          ['_dispatch_min', product.standardMinDispatch.toString()],
          ['_dispatch_max', product.standardMaxDispatch.toString()],
          ['_cutoff_time', product.standardCutoffTime],
          ['_cutoff_timezone', product.standardCutoffTimezone],
        ]),
      });
    }

    remove([id]);
  };

const partitionArray = <T>(
  array: Array<T>,
  index: number,
): [Array<T>, Array<T>] => [array.slice(0, index), array.slice(index)];

export const makeRecommendedItemsSeq = (
  items: RecommendationsT,
): Array<SearchOffers> => {
  const { headphone, deviceCase, accessory, screenProtector, general } = items;

  const [firstHeadPhone, restOfHeadPhones] = partitionArray(headphone, 1);
  const [firstScreenProtector, restOfScreenProtectors] = partitionArray(
    screenProtector,
    1,
  );
  const [first4Cases, restOfCases] = partitionArray(deviceCase, 4);
  const [first2Accessories, restOfAccessories] = partitionArray(accessory, 2);

  const recommendedItems = [
    ...firstScreenProtector,
    ...firstHeadPhone,
    ...first4Cases,
    ...first2Accessories,
    ...restOfCases,
    ...restOfAccessories,
    ...restOfHeadPhones,
    ...restOfScreenProtectors,
    ...general,
  ].filter(Boolean);

  return recommendedItems;
};

export const mergeAllPopularItems = (recommendedItems?: RecommendedItemT[]) => {
  if (!recommendedItems) return [];

  return uniqBy(
    recommendedItems.reduce(
      (prev, { recommendations: { popular } }) => [...prev, ...popular],
      [] as SearchOffers[],
    ),
    'title',
  );
};

export const handleQtyChange =
  ({ lineId, update, availableQty, lineItem }: QtyChangeArgs) =>
  (value: number | string) => {
    const newValue =
      Number(value) > availableQty ? availableQty : Number(value);

    if (newValue < 0) return;

    const normalizedFieldValue = value === 0 ? '' : newValue;

    if (
      typeof normalizedFieldValue === 'number' &&
      typeof newValue === 'number' &&
      lineId != null &&
      lineItem != null
    ) {
      const updates = [
        {
          id: lineId,
          quantity: newValue,
          attributes: lineItem.attributes as Array<CartItemAttribute>,
        },
      ];

      update(updates);
    }
  };

type GetFromCartType = {
  cart: Cart;
  recommendedItem: SearchOffers;
};

export const getItemFromCart = ({ cart, recommendedItem }: GetFromCartType) => {
  const {
    lines: { edges },
  } = cart;
  const nodes = edges.map((edge) => edge.node);

  const [selectedItem] = nodes.filter(
    ({ merchandise }) => merchandise.product.title === recommendedItem.title,
  );

  return selectedItem || {};
};

export const getItemQtyInCart = ({
  cart,
  recommendedItem,
}: GetFromCartType) => {
  const selectedItem = getItemFromCart({ cart, recommendedItem });

  if (selectedItem.quantity) return selectedItem.quantity;

  return 0;
};
